import { useState } from "react";
import {
    Card,
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    TextField,
    CircularProgress,
} from "@mui/material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import {
    WarningAmber as WarningIcon,
    InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import COLORS from "../../../../../constants/colors";
import { toast } from "react-toastify";
import baseURL from "../../../user/config";

const getCharges = (amount) => {
    const val = parseFloat(amount);
    if (isNaN(val) || val <= 0) return null;

    const percentageCharge = val * 0.0425;
    let mobileMoneyFee = 0;
    if (val < 200000) {
        mobileMoneyFee = 3000;
    } else if (val <= 999999) {
        mobileMoneyFee = 5000;
    } else {
        mobileMoneyFee = 10000;
    }
    const totalCharge = percentageCharge + mobileMoneyFee;
    const netAmount = val - totalCharge;

    return {
        percentageCharge,
        mobileMoneyFee,
        totalCharge,
        netAmount,
    };
};

const WithdrawFundsCard = ({ summaryData, onWithdrawSuccess }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [processingWithdrawal, setProcessingWithdrawal] = useState(false);
    const balance = summaryData?.userBalance || 0;
    const phoneNumber = summaryData?.phone || "+256 77X XXX XXX";

    const formatCurrency = (val) => `UGX ${Math.trunc(parseFloat(val)).toLocaleString("en-UG")}`;

    const handleOpenDialog = () => setIsDialogOpen(true);
    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setWithdrawAmount("");
    };

    const handleWithdrawAmountChange = (e) => {
        setWithdrawAmount(e.target.value);
    };

    const calculateCharges = (amount) => {
        const charges = getCharges(amount);
        if (!charges || charges.netAmount <= 0) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#F8D7DA",
                        p: 2,
                        borderRadius: 2,
                        mb: 2,
                        mt: 2,
                        boxShadow: 0,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <WarningIcon sx={{ color: "#842029", mr: 1 }} />
                    <Typography variant="body2" sx={{ color: "#842029" }}>
                        Your balance is too low to cover withdrawal charges for this amount.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ mt: 3, p: 2, border: "1px solid #E0E0E0", borderRadius: 2, background: "#F9FAFB" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#444" }}>
                    Platform Fee (4.25%):{" "}
                    <Typography component="strong" sx={{ fontWeight: "bold", color: "#222" }}>
                        {formatCurrency(charges.percentageCharge)}
                    </Typography>
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#444" }}>
                    Mobile Money Fee:{" "}
                    <Typography component="strong" sx={{ fontWeight: "bold", color: "#222" }}>
                        {formatCurrency(charges.mobileMoneyFee)}
                    </Typography>
                </Typography>
                <Divider sx={{ my: 1, borderColor: "#eee" }} />
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#444" }}>
                    Total Charges:{" "}
                    <Typography component="strong" sx={{ fontWeight: "bold", color: "#222" }}>
                        {formatCurrency(charges.totalCharge)}
                    </Typography>
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: COLORS.primary, mt: 1 }}>
                    Net Amount You Will Receive:{" "}
                    <Typography component="span" sx={{ fontWeight: "bold", color: COLORS.primary }}>
                        {formatCurrency(charges.netAmount)}
                    </Typography>
                </Typography>
            </Box>
        );
    };

    const handleConfirmWithdraw = async () => {
        const amountToWithdraw = parseFloat(withdrawAmount);

        if (isNaN(amountToWithdraw) || amountToWithdraw < 10000) {
            toast.error("Minimum withdrawal is UGX 10,000.");
            return;
        }
        if (amountToWithdraw > balance) {
            toast.error("Invalid amount or exceeds balance.");
            return;
        }

        setProcessingWithdrawal(true);

        try {
            const charges = getCharges(amountToWithdraw);
            if (!charges || charges.netAmount <= 0) {
                toast.error("Insufficient balance to cover withdrawal charges.");
                setProcessingWithdrawal(false);
                return;
            }

            const response = await fetch(`${baseURL}/org/withdraw`, {
                method: "POST",
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    amount: amountToWithdraw,
                    system_charge: charges.percentageCharge,
                    withdraw_charge: charges.mobileMoneyFee,
                    net_amount: charges.netAmount,
                    phone_number: phoneNumber,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(data.message || "Withdrawal request submitted successfully!");
                handleCloseDialog();
                if (onWithdrawSuccess) {
                    onWithdrawSuccess();
                }
            } else {
                toast.error(data.message || "Failed to process withdrawal.");
            }
        } catch (error) {
            console.error("Error during withdrawal:", error);
            toast.error("Error processing withdrawal. Try again later.");
        } finally {
            setProcessingWithdrawal(false);
        }
    };

    return (
        <>
            <Card
                sx={{
                    p: 2,
                    display: "flex",
                    mt: 4,
                    alignItems: "center",
                    borderRadius: 2,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                    border: `1px solid ${COLORS.border}`,
                    bgcolor: "#fff",
                }}
            >
                <>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: "50%",
                            bgcolor: "#ffebee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                        }}
                    >
                        <PaidOutlinedIcon sx={{ color: "red" }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            Current Balance
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                            {formatCurrency(balance)}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleOpenDialog}
                        sx={{ textTransform: "none", boxShadow: 0, borderRadius: 1.5, backgroundColor: '#2D4A3D',
                            '&:hover': {
                                backgroundColor: '#2D4A3D',
                                opacity: 0.9,
                                boxShadow: 0,
                            },
                        }}
                        disabled={balance < 10000}
                    >
                        Withdraw Funds
                    </Button>
                </>
            </Card>

            <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: "bold", pb: 1, fontSize: "1.3rem" }}>
                    Confirm Withdrawal Request
                </DialogTitle>
                <Divider sx={{ mx: 2 }} />
                <DialogContent sx={{ py: 3, px: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, color: "#333" }}>
                        Available Balance:{" "}
                        <Typography component="strong" sx={{ fontWeight: "bold", color: COLORS.primary }}>
                            {formatCurrency(balance)}
                        </Typography>
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            bgcolor: "#e6f7ff",
                            p: 2,
                            borderRadius: 2,
                            mb: 2,
                            border: "1px solid #91d5ff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                    >
                        <InfoIcon sx={{ color: "#1890ff", mr: 1 }} />
                        <Typography variant="body2" sx={{ color: "#2f54eb" }}>
                            Funds will be sent to your registered number:{" "}
                            <Typography component="strong" sx={{ fontWeight: "bold" }}>
                                {phoneNumber}
                            </Typography>
                            . To update this, please send us an email at support@yambaafrica.com or send us a ticket.
                        </Typography>
                    </Box>

                    <TextField
                        label="Amount to withdraw (UGX)"
                        fullWidth
                        variant="outlined"
                        type="number"
                        value={withdrawAmount}
                        onChange={handleWithdrawAmountChange}
                        inputProps={{ min: 10000, max: balance }}
                        sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        helperText={
                            withdrawAmount && parseFloat(withdrawAmount) > balance
                                ? "Amount exceeds your available balance."
                                : parseFloat(withdrawAmount) > 0 && parseFloat(withdrawAmount) < 10000
                                ? "Minimum withdrawal is UGX 10,000."
                                : ""
                        }
                        error={
                            (withdrawAmount && parseFloat(withdrawAmount) > balance) ||
                            (parseFloat(withdrawAmount) > 0 && parseFloat(withdrawAmount) < 10000)
                        }
                    />
                    {withdrawAmount && calculateCharges(withdrawAmount)}
                    <Typography variant="body2" sx={{ color: "#777", mt: 3, fontSize: "0.85rem" }}>
                        Note: Our team will review this withdrawal request and may contact you for confirmation before processing the funds. This process usually takes 1-2 business days.
                    </Typography>
                </DialogContent>
                <Divider sx={{ mx: 2 }} />
                <DialogActions sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        color="inherit"
                        disabled={processingWithdrawal}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2.5,
                            "&:hover": { borderColor: COLORS.primary, color: COLORS.primary },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmWithdraw}
                        variant="contained"
                        color="primary"
                        disabled={
                            !withdrawAmount ||
                            parseFloat(withdrawAmount) <= 0 ||
                            parseFloat(withdrawAmount) < 10000 ||
                            parseFloat(withdrawAmount) > balance ||
                            (getCharges(withdrawAmount) && getCharges(withdrawAmount).netAmount <= 0)
                        }
                        sx={{
                            textTransform: "none",
                            boxShadow: 0,
                            borderRadius: 1.5,
                            backgroundColor: '#2D4A3D',
                            '&:hover': {
                                backgroundColor: '#2D4A3D',
                                opacity: 0.9,
                                boxShadow: 0,
                            },
                        }}
                    >
                        {processingWithdrawal ? (
                            <>
                                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                                Just a sec...
                            </>
                        ) : (
                            "Confirm Withdraw"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WithdrawFundsCard;